import React, { useEffect, useRef, useState } from "react";
import { Bell, Menu, User } from "lucide-react";
import Metabuscador from "../Metabuscador";

export const DevBanner = React.memo(function DevBanner({ visible, height }) {
  if (!visible) return null;
  return (
    <div
      id="dev-banner"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        background: "#4f46e5",
        color: "white",
        fontSize: 14,
        padding: "8px 16px",
        textAlign: "center",
        boxShadow: "0 1px 4px rgba(0,0,0,.2)",
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <b>Modo DESARROLLO&nbsp;</b>activo: autenticación deshabilitada para
      facilitar cambios.
    </div>
  );
});

function WindowsMark() {
  return (
    <div className="w-8 h-8">
      <img src="/ucacue-logo.png" alt="UCACUE" className="object-contain" />
    </div>
  );
}

const AppHeader = React.memo(function AppHeader({
  onToggleSidebar,
  onToggleNoticias,
  noticiasOpen,
  buildEzproxyLoginUrl,
  clearAuthAll,
  getAuthSnapshot,
  modoDesarrollo,
  devBannerHeight,
  headerBaseHeight,
  buttonRef,
}) {
  const top = modoDesarrollo ? devBannerHeight : 0;

  const [auth, setAuth] = useState(getAuthSnapshot());
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const refresh = () => setAuth(getAuthSnapshot());
    window.addEventListener("auth_changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("auth_changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [getAuthSnapshot]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <header
      id="app-header"
      className="fixed left-0 right-0 z-[9999] bg-white/95 backdrop-blur border-b border-gray-200"
      style={{ top, height: headerBaseHeight }}
    >
      <div className="h-full px-3 sm:px-4 md:px-6 flex items-center gap-3">
        <button
          onClick={onToggleSidebar}
          className="inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:bg-gray-100"
          aria-label="Abrir/cerrar menú"
          title="Menú"
        >
          <Menu className="w-5 h-5" />
        </button>

        <a href="/" className="flex items-center gap-2 min-w-0">
          <WindowsMark />
          <div className="leading-tight hidden sm:block">
            <div className="text-[13px] text-gray-500">UCACUE</div>
            <div className="text-[15px] font-semibold text-gray-900 -mt-[2px]">
              Biblioteca Virtual
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          <a
            className="px-3 py-2 text-sm rounded-full bg-gray-900 text-white"
            href="/"
          >
            Inicio
          </a>
          <a
            className="px-3 py-2 text-sm rounded-full hover:bg-gray-100 text-gray-700"
            href="#acerca"
          >
            Acerca de
          </a>
        </nav>

        <div className="hidden md:flex flex-1 min-w-0 items-center justify-center">
          <Metabuscador />
        </div>

        <div className="flex items-center gap-1">
          <button
            ref={buttonRef}
            onClick={onToggleNoticias}
            className="relative inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:bg-gray-100"
            aria-label="Abrir/cerrar noticias"
            title="Noticias"
          >
            <Bell className="w-5 h-5" />
            {noticiasOpen && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full" />
            )}
          </button>

          {(!auth.token && !auth.guest && !auth.anon) ? (
            <>
              <a
                href={buildEzproxyLoginUrl(`${window.location.pathname}${window.location.search}`)}
                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full hover:bg-gray-100 text-sm text-gray-700"
                title="Iniciar sesión"
              >
                <User className="w-4 h-4" />
                Iniciar sesión
              </a>

              <a
                href={buildEzproxyLoginUrl(`${window.location.pathname}${window.location.search}`)}
                className="sm:hidden inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:bg-gray-100"
                aria-label="Iniciar sesión"
                title="Iniciar sesión"
              >
                <User className="w-5 h-5" />
              </a>
            </>
          ) : (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-full hover:bg-gray-100 text-sm text-gray-700"
                title="Perfil"
              >
                <User className="w-4 h-4" />
                <span>Perfil: {auth.label}</span>
              </button>

              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="sm:hidden inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:bg-gray-100"
                aria-label="Perfil"
                title="Perfil"
              >
                <User className="w-5 h-5" />
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl border bg-white shadow-lg overflow-hidden z-50">
                  <div className="px-4 py-3 border-b">
                    <div className="text-sm font-semibold text-gray-900">Perfil: {auth.label}</div>
                    <div className="text-xs text-gray-500">
                      {auth.anon ? "Acceso anónimo" : auth.guest ? "Acceso invitado" : "Acceso institucional"}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 text-rose-600"
                    onClick={() => {
                      setMenuOpen(false);
                      clearAuthAll();
                      window.location.href = "https://login.vpn.ucacue.edu.ec/logout";
                    }}
                  >
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </header>
  );
});

export default AppHeader;
